import React from 'react';
import styled from 'styled-components';
import { Icon, StackPanel, typographyPresets } from '@agilepoint/wat-react-ux';
import { INotificationDetail } from 'modules/core/types';
import { useCustomPopupEvents } from 'lib/hooks';
import { useConfigScopeSelectors } from 'modules/core/scopes';
import { EditAnnouncementContainer } from 'modules/core/containers';
import { commonStyles } from 'common';

const Title = styled.div`
    ${typographyPresets.headings.h6}
    font-size: 14px;
    width: 90%;
    ${commonStyles.textOverflow}
`;

const StyledStackPanel = styled(StackPanel)`
    padding: 16px 32px 4px 18px;
    cursor: pointer;
    position: relative;
    :hover {
        ${Title} {
            color: #0077D9 ;
        }
    }
`;

const SubTitle = styled.div`
    ${typographyPresets.textStyles.small}
    font-size: 12px;
`;

const Date1 = styled.div`
    ${typographyPresets.textStyles.lightSmall}
    font-size: 12px;
`;

const StyledIcon = styled(Icon)`
    position: absolute;
    right: 12px;
`;

const NotificationContent = React.memo((props: { content: string }) => (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: props.content }}></div>
));

export const NotificationListItem = React.memo((props: { notificationDetail: INotificationDetail, showHideCallout: (value: boolean) => void }) => {
    const { notificationDetail: { title, subTitle, createdOn, content, id }, showHideCallout } = props;
    const { showCustomPopup, removePopup } = useCustomPopupEvents();
    const { isUserTypeAdmin } = useConfigScopeSelectors();
    const isAdminTypeUser = isUserTypeAdmin();

    const renderAnnouncement = React.useCallback(() => {
        showHideCallout(false);
        showCustomPopup({
            onRenderContent: () => <NotificationContent content={content} />,
            headerTitle: `${title} - ${subTitle}`,
            width: '700px',
            height: '500px',
            showModal: true
        });
    }, [content, showCustomPopup, showHideCallout, subTitle, title]);

    const renderEditAnnouncement = React.useCallback((ev: React.MouseEvent<Element, MouseEvent>) => {
        ev.stopPropagation();
        showHideCallout(false);
        showCustomPopup({
            onRenderContent: () => <EditAnnouncementContainer id={id} removePopup={removePopup} />,
            headerTitle: `${title} - ${subTitle}`,
            id: 'edit-announcement',
            width: '700px',
            showModal: true
        });
    }, [id, removePopup, showCustomPopup, showHideCallout, subTitle, title]);

    return (
        <StyledStackPanel orientation="vertical" width="calc(100% - 64px)" height='calc(100% - 24px)' justifyContent='left' gap='8' onClick={renderAnnouncement}>
            {
                isAdminTypeUser ? <EditNotification renderEditAnnouncement={renderEditAnnouncement} /> : <></>
            }
            <Title>{title}</Title>
            <SubTitle>{subTitle}</SubTitle>
            <StackPanel width='100%' orientation='horizontal' alignment='center' gap="8">
                <Icon iconName='ap-calendar.solid' iconSize="16" />
                <Date1>{new Date(Number(createdOn.substring(6, 19))).toLocaleString()}</Date1>
            </StackPanel>
        </StyledStackPanel>
    );
});

const EditNotification = React.memo((props: { renderEditAnnouncement: (ev: React.MouseEvent<Element, MouseEvent>) => void }) => {
    const { renderEditAnnouncement } = props;
    return (
        <StyledIcon iconName='ap-pencil.solid' iconSize="16" onClick={renderEditAnnouncement} />
    );
});
